import React, { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Table } from '@reapitform/common-ui'
import { toast } from '@reapit/elements/core/toaster'
import { AdminFormListItem, EnumCatalog } from '../../../../api/types'
import { getFormFamily, useAdminEnums } from '../../../../api/enums'
import { useAdminFormsList } from '../../../../api/forms'
import { useBuilderDeletes } from '../../../../api/builder-deletes'
import { useCopyFormVersion } from '../../../../api/form-versions'
import { useAccessMode } from '../../../../access-mode-provider'
import { ConfirmDeleteDialog } from '../../../../components/confirm-delete-dialog'
import { ConfirmCopyDialog } from '../../../../components/confirm-copy-dialog'
import { getFormsRowActions, formsTableConfig, getFormsTableHeaders } from './forms-table-config'
import { FAMILY_FILTER_PARAM } from '../form-type-tiles'

const matchesQuery = (form: AdminFormListItem, query: string): boolean => {
  const haystack = `${form.name} ${form.fullName ?? ''}`.toLowerCase()
  return haystack.includes(query.toLowerCase())
}

/**
 * Whether a form row survives the active family, sub-type and search filters — pure, so the URL-param
 * plumbing in the component can be tested separately from this actual matching logic.
 */
export const matchesFilters = (
  form: AdminFormListItem,
  enums: EnumCatalog | null,
  activeFamily: string | null,
  activeType: string | null,
  query: string,
): boolean => {
  const familyMatch = !activeFamily || getFormFamily(enums, form.formType) === activeFamily
  const typeMatch = !activeType || form.formType === activeType
  const queryMatch = !query || matchesQuery(form, query)
  return familyMatch && typeMatch && queryMatch
}

/** URL param controlling the "show only current version" toggle in the Toolbar. Absent/anything other than
 * 'all' means the default (current-only) — so the filter is on by default without needing a URL param. */
export const VERSION_FILTER_PARAM = 'versionFilter'
export const VERSION_FILTER_ALL_VALUE = 'all'

/**
 * Collapses a form-type/search-filtered row list down to one row per form: whichever version is actually
 * "current" right now. Mirrors the backend's own Form.CurrentVersion rule (latest validFromUTC that's
 * already passed) so "current" means the same thing here as it does everywhere else in the system. A form
 * with no version valid yet (all future-dated) falls back to its soonest upcoming version, so it still gets
 * a row instead of disappearing from the grid entirely.
 */
export const toCurrentVersionOnly = (rows: AdminFormListItem[]): AdminFormListItem[] => {
  const nowISO = new Date().toISOString()
  const byForm = new Map<string, AdminFormListItem[]>()
  rows.forEach((row) => {
    const group = byForm.get(row.referenceID)
    if (group) group.push(row)
    else byForm.set(row.referenceID, [row])
  })

  return Array.from(byForm.values()).map((group) => {
    const live = group.filter((row) => row.validFromUTC && row.validFromUTC <= nowISO)

    if (live.length > 0) {
      // Latest version that's already gone live — the real "current" one.
      return live.reduce((latest, row) => {
        return row.validFromUTC! > latest.validFromUTC! ? row : latest
      })
    }

    // Nothing's live yet for this form — fall back to the soonest upcoming version instead of dropping
    // the form from the grid entirely. Rows with no date at all sort last (never preferred over a dated one).
    return group.reduce((soonest, row) => {
      if (!row.validFromUTC) return soonest
      if (!soonest.validFromUTC) return row
      return row.validFromUTC < soonest.validFromUTC ? row : soonest
    })
  })
}

/**
 * The forms grid. Reads the full forms list once (shared/deduped with the tiles) and applies the active
 * form-type filter + search query from the URL client-side. Row click → builder route is wired in FE-14.
 */
export const FormsTable: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { data: enums } = useAdminEnums()
  const { data: forms, loading, errorString, refresh } = useAdminFormsList()
  const { deleteForm, deleteFormVersion } = useBuilderDeletes()
  const copyFormVersion = useCopyFormVersion()
  const { isReadOnly } = useAccessMode()

  const [pendingDelete, setPendingDelete] = useState<AdminFormListItem | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [pendingCopy, setPendingCopy] = useState<AdminFormListItem | null>(null)
  const [copying, setCopying] = useState(false)

  const activeFamily = searchParams.get(FAMILY_FILTER_PARAM)
  const activeType = searchParams.get('filterByType')
  const query = searchParams.get('query') ?? ''
  const currentVersionOnly = searchParams.get(VERSION_FILTER_PARAM) !== VERSION_FILTER_ALL_VALUE

  const rowActions = useMemo(
    () => getFormsRowActions(navigate, setPendingDelete, setPendingCopy, isReadOnly),
    [navigate, isReadOnly],
  )

  const rows = useMemo(() => {
    if (!forms) return null
    const filtered = forms.filter(
      (form) =>
        matchesFilters(form, enums, activeFamily, activeType, query) &&
        // Exclude rows with no versionReferenceID — they have no actionable version and
        // would produce a null/duplicate row key in the table.
        Boolean(form.versionReferenceID),
    )

    return (currentVersionOnly ? toCurrentVersionOnly(filtered) : filtered).sort((a, b) => {
      // Primary: group by region (alphabetical on the raw enum value string)
      const regionCompare = (a.region ?? '').localeCompare(b.region ?? '')
      if (regionCompare !== 0) return regionCompare
      // Secondary: sort by valid-from date ascending; null dates sorted last
      if (!a.validFromUTC && !b.validFromUTC) return 0
      if (!a.validFromUTC) return 1
      if (!b.validFromUTC) return -1
      return a.validFromUTC.localeCompare(b.validFromUTC)
    })
  }, [forms, activeFamily, activeType, query, currentVersionOnly, enums])

  const confirmCopy = async () => {
    if (!pendingCopy?.versionReferenceID) return
    setCopying(true)
    try {
      const newVersion = await copyFormVersion(pendingCopy.referenceID, pendingCopy.versionReferenceID)
      toast.success(`Copied "${pendingCopy.name}" into a new version.`)
      setPendingCopy(null)
      navigate(`/forms/${pendingCopy.referenceID}/versions/${newVersion.referenceID}`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not copy this version.')
    } finally {
      setCopying(false)
    }
  }

  // A form must always keep at least one version, so deleting the last one deletes the whole form instead
  // (the backend enforces this too — see DeleteFormVersionCommandHandler's 409 guard).
  const isWholeFormDelete = (pendingDelete?.versionCount ?? 0) <= 1

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      if (isWholeFormDelete) {
        await deleteForm(pendingDelete.referenceID)
        toast.success(`"${pendingDelete.name}" deleted.`)
      } else if (pendingDelete.versionReferenceID) {
        await deleteFormVersion(pendingDelete.referenceID, pendingDelete.versionReferenceID)
        toast.success(`"${pendingDelete.name}" (${pendingDelete.version}) deleted.`)
      }
      setPendingDelete(null)
      refresh()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not delete this.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <>
      <Table<AdminFormListItem>
        headers={getFormsTableHeaders(enums)}
        {...formsTableConfig}
        rowKeyIdentifier="versionReferenceID"
        rows={rows}
        isLoading={loading}
        isError={Boolean(errorString)}
        errorMessage={errorString ?? undefined}
        tableRowAction={rowActions}
      />
      <ConfirmDeleteDialog
        isOpen={Boolean(pendingDelete)}
        title={isWholeFormDelete ? 'Delete form' : 'Delete version'}
        message={
          isWholeFormDelete
            ? `Delete "${pendingDelete?.name}" (${pendingDelete?.version})? This is the form's only version, so the whole form — including its review cards, fields, recipient content, PDF templates, and notification settings — will be permanently deleted. This can't be undone.`
            : `Delete "${pendingDelete?.name}" (${pendingDelete?.version})? This removes just this version — its review cards, fields, recipient content, PDF templates, and notification settings — permanently. The form's other versions won't be affected. This can't be undone.`
        }
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
      <ConfirmCopyDialog
        isOpen={Boolean(pendingCopy)}
        title="Copy version"
        message={`Copy "${pendingCopy?.name}" (${pendingCopy?.version}) into a new version? This duplicates its review cards, fields, recipient content, PDF templates, and notification settings into a brand-new version, which you can then edit independently. You'll be taken straight to it in the builder.`}
        busy={copying}
        onConfirm={confirmCopy}
        onCancel={() => setPendingCopy(null)}
      />
    </>
  )
}
